// src/i18nProvider.jsx (puede ser el mismo archivo que tienes)
import React from 'react';
import PropTypes from 'prop-types';

import i18next from 'i18next';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import LngDetector from 'i18next-browser-languagedetector';
import { attachDocumentLanguageSync } from '../../utils/documentLanguage';
import { toUiLanguage } from '../../utils/locale/language';

// Traducciones
import common_es from './translations/es/common.json';
import onboarding_es from './translations/es/onboarding.json';
import evaluations_es from './translations/es/evaluations.json';
import students_es from './translations/es/students.json';
import notifications_es from './translations/es/notifications.json';
import feedback_es from './translations/es/feedback.json';
import error_es from './translations/es/error.json';
import dashboards_es from './translations/es/dashboards.json';
import lti_es from './translations/es/lti.json';
import feedback_assistance_es from './translations/es/feedback_assistance.json';
import evaluation_status_es from './translations/es/evaluation_status.json';

import common_en from './translations/en/common.json';
import onboarding_en from './translations/en/onboarding.json';
import evaluations_en from './translations/en/evaluations.json';
import students_en from './translations/en/students.json';
import notifications_en from './translations/en/notifications.json';
import feedback_en from './translations/en/feedback.json';
import error_en from './translations/en/error.json';
import dashboards_en from './translations/en/dashboards.json';
import lti_en from './translations/en/lti.json';
import feedback_assistance_en from './translations/en/feedback_assistance.json';
import evaluation_status_en from './translations/en/evaluation_status.json';


import common_tr from './translations/tr/common.json';
import onboarding_tr from './translations/tr/onboarding.json';
import evaluations_tr from './translations/tr/evaluations.json';
import students_tr from './translations/tr/students.json';
import notifications_tr from './translations/tr/notifications.json';
import feedback_tr from './translations/tr/feedback.json';
import error_tr from './translations/tr/error.json';
import dashboards_tr from './translations/tr/dashboards.json';
import lti_tr from './translations/tr/lti.json';
import feedback_assistance_tr from './translations/tr/feedback_assistance.json';
import evaluation_status_tr from './translations/tr/evaluation_status.json';

import twoFactor_es from './translations/es/twoFactor.json';
import twoFactor_en from './translations/en/twoFactor.json';
import twoFactor_tr from './translations/tr/twoFactor.json';

import common_pt from './translations/pt/common.json';
import onboarding_pt from './translations/pt/onboarding.json';
import evaluations_pt from './translations/pt/evaluations.json';
import students_pt from './translations/pt/students.json';
import notifications_pt from './translations/pt/notifications.json';
import feedback_pt from './translations/pt/feedback.json';
import twoFactor_pt from './translations/pt/twoFactor.json';
import error_pt from './translations/pt/error.json';
import dashboards_pt from './translations/pt/dashboards.json';
import lti_pt from './translations/pt/lti.json';
import feedback_assistance_pt from './translations/pt/feedback_assistance.json';
import evaluation_status_pt from './translations/pt/evaluation_status.json';



// Opciones detector (si lo usas)
const detectionOptions = {
  order: ['querystring', 'cookie', 'localStorage', 'navigator', 'htmlTag', 'path', 'subdomain'],
  lookupQuerystring: 'lng',
  lookupCookie: 'i18next',
  lookupLocalStorage: 'i18nextLng',
  lookupFromPathIndex: 0,
  lookupFromSubdomainIndex: 0,
  caches: ['localStorage', 'cookie'],
  excludeCacheFor: ['cimode'],
  cookieMinutes: 10,
  cookieDomain: 'myDomain',
  htmlTag: document.documentElement,
};

// Inicializa i18n con plugins
i18next
  .use(LngDetector)            // 👈 habilita detección (opcional)
  .use(initReactI18next)       // 👈 enlaza con React (obligatorio)
  .init({
    // Normalized because users.locale holds legacy values ('us', 'es_ES') that i18next has
    // no resources for: it silently fell back to Spanish and wrote them into <html lang>.
    lng: toUiLanguage(localStorage.getItem('locale')),
    fallbackLng: 'es',
    ns: [
      'common', 'onboarding', 'evaluations', 'students', 'notifications',
      'feedback', 'error', 'dashboards', 'lti', 'feedback_assistance', 'evaluation_status',
    ],
    defaultNS: 'common',
    resources: {
      es: {
        common: common_es,
        onboarding: onboarding_es,
        evaluations: evaluations_es,
        students: students_es,
        notifications: notifications_es,
        feedback: feedback_es,
        error: error_es,
        dashboards: dashboards_es,
        lti: lti_es,
        feedback_assistance: feedback_assistance_es,
        evaluation_status: evaluation_status_es,
        translations: twoFactor_es
      },
      en: {
        common: common_en,
        onboarding: onboarding_en,
        evaluations: evaluations_en,
        students: students_en,
        notifications: notifications_en,
        feedback: feedback_en,
        error: error_en,
        dashboards: dashboards_en,
        lti: lti_en,
        feedback_assistance: feedback_assistance_en,
        evaluation_status: evaluation_status_en,
        translations: twoFactor_en
      },

      tr: {
        common: common_tr, // 'common' is our custom namespace
        evaluations: evaluations_tr,
        notifications: notifications_tr,
        students: students_tr,
        feedback: feedback_tr,
        error: error_tr,
        dashboards: dashboards_tr,
        lti: lti_tr,
        feedback_assistance: feedback_assistance_tr,
        evaluation_status: evaluation_status_tr,
        onboarding: onboarding_tr,
        translations: twoFactor_tr
      },

      pt: {
        common: common_pt,
        onboarding: onboarding_pt,
        evaluations: evaluations_pt,
        students: students_pt,
        notifications: notifications_pt,
        feedback: feedback_pt,
        error: error_pt,
        dashboards: dashboards_pt,
        lti: lti_pt,
        feedback_assistance: feedback_assistance_pt,
        evaluation_status: evaluation_status_pt,
        translations: twoFactor_pt
      },

    },
    detection: detectionOptions,          // solo si usas LngDetector
    interpolation: { escapeValue: false },// React ya hace escape
    react: { useSuspense: false },        // evita Suspense por ahora
  });

// i18next only READS documentElement (via `detection.htmlTag` above), it never writes
// it. Keeping <html lang> in sync is what stops Chrome from auto-translating the page
// and crashing React's reconciliation. See utils/documentLanguage for the full why.
attachDocumentLanguageSync(i18next);

export default function I18nextProviderC({ children }) {
  return <I18nextProvider i18n={i18next}>{children}</I18nextProvider>;
}

I18nextProviderC.propTypes = { children: PropTypes.node };
